// SPDX-License-Identifier: AGPL-3.0-only
import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Code,
  Group,
  Loader,
  MultiSelect,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';

import { ApiError } from '../../api/client';
import type { EventSeverity, LogEvent, LogFilter, LogPage } from '../../api/observability';

/**
 * The operational timeline and device transcript.
 *
 * Shared by the Device Logs and Gateway Logs views: the events are the same
 * shape and the only difference is which one the Server was asked for, so a
 * second component would be the same code with a different import.
 *
 * # WHAT AN OPERATOR IS MEANT TO BE ABLE TO FOLLOW
 *
 *     the operation started
 *     a fresh session opened
 *     the host key was the confirmed one
 *     DEVICE RX: Username:
 *     DEVICE TX: <CREDENTIAL REDACTED>
 *     DEVICE TX: show interface Ethernet 1/0/5 status
 *     DEVICE RX: eth1/0/5   notconnect
 *     the driver definition interpreted the answer
 *     verification started, over a session of its own
 *     verification completed
 *     SUCCESS
 *
 * # DEVICE OUTPUT IS UNTRUSTED INPUT
 *
 * Everything in `body` came from a switch. It is rendered as TEXT — through
 * React's own escaping, never `dangerouslySetInnerHTML` — so a device that
 * prints markup prints markup rather than becoming it. The Server has already
 * made control characters visible; this side must not undo that by asking a
 * browser to interpret them.
 */
export function LogViewer({
  load,
  emptyMessage,
  download,
}: {
  load: (filter: LogFilter) => Promise<LogPage>;
  emptyMessage: string;
  /**
   * Fetch the whole bounded history as a ZIP, if this view offers one.
   *
   * Optional so a future embedding of this viewer that has nothing to export
   * simply omits it, rather than being given a button that fails.
   */
  download?: () => Promise<void>;
}) {
  const [page, setPage] = useState<LogPage | null>(null);
  const [older, setOlder] = useState<LogEvent[]>([]);
  const [severity, setSeverity] = useState<string[]>([]);
  const [showTranscript, setShowTranscript] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const filter = useCallback(
    (cursor?: string): LogFilter => ({
      severity: severity.length ? (severity as EventSeverity[]) : undefined,
      category: showTranscript ? undefined : ['operational'],
      cursor,
    }),
    [severity, showTranscript],
  );

  const refresh = useCallback(async () => {
    setBusy(true);
    try {
      setPage(await load(filter()));
      setOlder([]);
      setError(null);
    } catch (err) {
      setError(err as ApiError);
      setPage(null);
    } finally {
      setBusy(false);
    }
  }, [load, filter]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /**
   * Fetch the next page.
   *
   * # WHY PAGES ACCUMULATE RATHER THAN REPLACE
   *
   * An operator following one operation is reading downwards through a
   * timeline, and replacing the list would lose the events they were reading.
   * The cursor is a keyset position, so appending cannot duplicate or skip.
   */
  const loadOlder = async () => {
    if (!page?.next_cursor) return;
    setBusy(true);
    try {
      const next = await load(filter(page.next_cursor));
      setOlder((prev) => [...prev, ...next.items]);
      setPage((prev) => (prev ? { ...prev, next_cursor: next.next_cursor } : next));
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Download the archive.
   *
   * # WHY THE BUTTON HAS ITS OWN BUSY STATE
   *
   * Because an export of thousands of events takes noticeably longer than a
   * page of them, and sharing `busy` would freeze the Refresh and Load-older
   * controls for the duration. An operator who clicks and sees nothing happen
   * clicks again, and each click is a full export the Server has to build.
   */
  const startDownload = async () => {
    if (!download) return;
    setDownloading(true);
    try {
      await download();
      setError(null);
    } catch (err) {
      // SURFACED, NOT SWALLOWED. A download that fails silently leaves the
      // operator waiting for a file that is never coming.
      setError(err as ApiError);
    } finally {
      setDownloading(false);
    }
  };

  const events = [...(page?.items ?? []), ...older];

  return (
    <Stack gap="sm">
      <Group gap="sm" align="flex-end">
        <MultiSelect
          label="Severity"
          data={['INFO', 'WARN', 'ERROR']}
          value={severity}
          onChange={setSeverity}
          clearable
          w={220}
          placeholder="All"
        />
        <Button
          variant={showTranscript ? 'filled' : 'default'}
          onClick={() => setShowTranscript((v) => !v)}
        >
          {showTranscript ? 'Hiding nothing' : 'Timeline only'}
        </Button>
        <Button variant="default" onClick={() => void refresh()} loading={busy}>
          Refresh
        </Button>
        {download && (
          <Tooltip
            multiline
            w={320}
            label={
              'Downloads the recorded history as a ZIP containing logs.jsonl, logs.txt ' +
              'and metadata.json. The archive is bounded by time and event count, and ' +
              'metadata.json states what it contains and anything it does not. ' +
              'Credential values are never included. The filters above are not applied.'
            }
          >
            <Button variant="default" onClick={() => void startDownload()} loading={downloading}>
              Download
            </Button>
          </Tooltip>
        )}
      </Group>

      {error && (
        <Alert color="red" title="Something went wrong">
          {error.message}
        </Alert>
      )}

      {!error && events.length === 0 && !busy && (
        <Text size="sm" c="dimmed">
          {emptyMessage}
        </Text>
      )}

      <Stack gap={4}>
        {events.map((event) => (
          <LogRow key={event.id} event={event} />
        ))}
      </Stack>

      {busy && events.length > 0 && <Loader size="sm" />}

      {page?.next_cursor && (
        <Button variant="subtle" onClick={() => void loadOlder()} loading={busy}>
          Load older
        </Button>
      )}

      {page && (
        <Text size="xs" c="dimmed">
          Showing {events.length} event{events.length === 1 ? '' : 's'}, newest first,
          {' '}up to {page.limit} per page.
        </Text>
      )}
    </Stack>
  );
}

/** One event. */
function LogRow({ event }: { event: LogEvent }) {
  const transcript = event.category === 'transcript';

  return (
    <Card withBorder padding="xs" bg={transcript ? 'var(--mantine-color-default-hover)' : undefined}>
      <Group gap="xs" wrap="nowrap" align="flex-start">
        <Text size="xs" c="dimmed" w={90} style={{ flexShrink: 0 }}>
          {timeOf(event.occurred_at)}
        </Text>

        <Badge size="sm" color={SEVERITY_COLOUR[event.severity]} variant="light" style={{ flexShrink: 0 }}>
          {event.severity}
        </Badge>

        <Stack gap={2} style={{ flexGrow: 1, minWidth: 0 }}>
          <Group gap="xs" wrap="nowrap">
            <Text size="sm" fw={500}>
              {DIRECTION[event.type] ?? readableType(event.type)}
            </Text>
            {event.phase && (
              <Text size="xs" c="dimmed">
                {event.phase}
              </Text>
            )}
            {typeof event.duration_ms === 'number' && event.duration_ms > 0 && (
              <Text size="xs" c="dimmed">
                {event.duration_ms} ms
              </Text>
            )}
            {event.fields?.outcome && (
              <Badge size="sm" color={OUTCOME_COLOUR[event.fields.outcome] ?? 'gray'}>
                {event.fields.outcome}
              </Badge>
            )}
          </Group>

          {/* THE PAYLOAD, AS TEXT.
              React escapes it. A device that prints markup prints markup. */}
          {event.body && (
            <Code block style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>
              {event.body}
            </Code>
          )}

          {/* A credential event has no body by construction; its message is the
              redaction marker, which is what an operator should see. */}
          {!event.body && event.message && (
            <Text size="sm" c={transcript ? undefined : 'dimmed'}>
              {event.message}
            </Text>
          )}

          {event.truncated && (
            <Text size="xs" c="orange">
              This entry was shortened — the device produced more than one event may hold.
            </Text>
          )}
        </Stack>
      </Group>
    </Card>
  );
}

const SEVERITY_COLOUR: Record<EventSeverity, string> = {
  INFO: 'blue',
  WARN: 'yellow',
  ERROR: 'red',
};

const OUTCOME_COLOUR: Record<string, string> = {
  SUCCESS: 'green',
  FAILED: 'red',
  /* UNKNOWN is deliberately NOT red and NOT green. It is neither, and colouring
     it as either would be the display making a claim the system refused to. */
  UNKNOWN: 'yellow',
};

/** The transcript types get the DEVICE RX / DEVICE TX form Directive 56 uses. */
const DIRECTION: Record<string, string> = {
  'device.rx': 'DEVICE RX',
  'device.tx': 'DEVICE TX',
  'device.tx.credential': 'DEVICE TX',
};

/** Turn a dotted event type into something readable without a lookup table. */
function readableType(type: string): string {
  return type.replace(/[._]/g, ' ');
}

function timeOf(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  return at.toLocaleTimeString();
}
